import React, { useEffect } from 'react';
import { BREAKPOINTS, Button, Selector, Stack, TextInput, rem, useLang } from "@manulife/mux";
import { colors } from '@manulife/mux';
import { NormalText, SectionHeadding } from '../../assets/appStyledComponents';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import IrAboutYouInputs from './IrAboutYouInputs';
import LABELS from '../../assets/labels';
import { eForWhom, eIncluded } from '../../services/helpers/udmConstants';
import NumberConstants from '../../assets/appStyledComponents/numberConstant.json';
import useWindowSize from '../../customHooks/useWindowSize';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { resetCalculatorDataResult, setAboutYouData } from '../../appRedux/IrCalculator/Actions';
import { addCurrencySign, getAmountNumbers, getOnlyNumbers } from '../../services/helpers/commonHelpers';
import { resetChecks } from '../../appRedux/IrResultCheckbox/Actions';
import { FormFocusProvider, useFormFocus } from '../../customHooks/useFormFocus';

function IrAboutYouFormContent() {
    const { width: windowWidth } = useWindowSize();
    const selectedLang = useLang();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const storedAboutYouData = useSelector(
        (state) => state.irCalculatorReducer.data.step1
    );
    const { focusField } = useFormFocus();

    const { num10, num20, num24, num32, num40 } = NumberConstants;
    const methods = useForm({
        defaultValues: {
            ...storedAboutYouData,
            you: {
                ...storedAboutYouData.you,
                irName: storedAboutYouData.you.irName === eForWhom.you ? "" : storedAboutYouData.you.irName,
                irAnnualIncome: addCurrencySign(getAmountNumbers(storedAboutYouData.you.irAnnualIncome), selectedLang)
            },
            partner: {
                ...storedAboutYouData.partner,
                irName: storedAboutYouData.partner.irName === eForWhom.partner ? "" : storedAboutYouData.partner.irName,
                irAnnualIncome: addCurrencySign(getAmountNumbers(storedAboutYouData.partner.irAnnualIncome), selectedLang)
            }
        },
        shouldFocusError: false 
    });
    const { formState: { isSubmitted }, control, handleSubmit, trigger, watch, setValue, getValues, setFocus } = methods;

    useEffect(() => {
        if (isSubmitted) {
            trigger();
        }
        return () => null
    }, [isSubmitted, selectedLang, trigger]);

    useEffect(() => {
        const you = getValues('you')
        const partner = getValues('partner')
        if (storedAboutYouData?.you?.irEmploymentStatus) {
            setValue("you", {
                ...you,
                irEmploymentStatus: storedAboutYouData?.you?.irEmploymentStatus
            })
        }
        if (storedAboutYouData?.partner?.irEmploymentStatus) {
            setValue("partner", {
                ...partner,
                irEmploymentStatus: storedAboutYouData?.partner?.irEmploymentStatus
            })
        }
    }, [getValues, setValue, storedAboutYouData?.you?.irEmploymentStatus,
        storedAboutYouData?.partner?.irEmploymentStatus]);

    useEffect(() => {
        const you = getValues('you')
        const partner = getValues('partner')

        if (storedAboutYouData?.you?.irAnnualIncome) {
            setValue("you", {
                ...you,
                irAnnualIncome: storedAboutYouData?.you?.irAnnualIncome
            })
        }
        if (storedAboutYouData?.partner?.irAnnualIncome) {
            setValue("partner", {
                ...partner,
                irAnnualIncome: storedAboutYouData?.partner?.irAnnualIncome
            })
        }

    }, [getValues, setValue, storedAboutYouData?.you?.irAnnualIncome,
        storedAboutYouData?.partner?.irAnnualIncome]);

    const selectedIrInsuranceFor = watch("irInsuranceFor");

    const enteredIrChildCount = watch("irChildCount");

    useEffect(() => {
        if (selectedIrInsuranceFor === eIncluded.justMe) {
            setValue("partner", {
                "irName": "",
                "irAge": "",
                "irSex": "",
                "irSmokingStatus": "",
                "irEmploymentStatus": "",
                "irAnnualIncome": "0"
            })
        }
        return () => null
    }, [selectedIrInsuranceFor, setValue])

    const fetchCalculation = (data) => {
        const { you: { irName: youName }, partner: { irName: partnerName } } = data;
        dispatch(resetChecks());
        dispatch(resetCalculatorDataResult());
        dispatch(setAboutYouData({
            ...data,
            you: {
                ...data.you,
                irName: youName === "" ? eForWhom.you : data.you.irName
            },
            partner: {
                ...data.partner,
                irName: partnerName === "" ? eForWhom.partner : data.partner.irName
            }
        }));
        navigate("/calculator");
    };      

    const handleError = (errors) => {
        const forWhom = Object.keys(errors)[0];
        const errorField = Object.keys(errors[forWhom])[0];
        // custom error handling if first error is on mentioned field
        switch (errorField) {
            case "irSmokingStatus":
                focusField(forWhom, 'smokingStatus');
                return;
            case "irSex":                
                focusField(forWhom, 'sex');                
                return;
            default:
                // let react-hook-form handle other field's error
                if (forWhom && errorField) {
                    const fullFieldPath = `${forWhom}.${errorField}`;
                    try {
                        setFocus(fullFieldPath);
                    } catch (e) {
                        console.warn(`Could not focus field: ${fullFieldPath}`, e);
                    }
                }
                return;
        }
    };

    return (
        <FormProvider {...methods}>
            <Stack as="form" align="flex-start" gap={`${rem(num40)}`}
                onSubmit={handleSubmit(fetchCalculation, handleError)}
                style={{
                    border: `1px solid ${colors.light_3_dark_navy}`,
                    background: `#FFF`,
                    padding: windowWidth > BREAKPOINTS.TABLET_PORTRAIT ? `${rem(num32)} ${rem(num40)}` : `${rem(num40)} ${rem(num24)}`,
                    alignSelf: `stretch`
                }}>
                <Stack align="flex-start" gap={`${rem(num20)}`}>
                    <SectionHeadding>{LABELS.aboutYouHeading[selectedLang]}</SectionHeadding>
                    <NormalText>{LABELS.aboutYouDescription1[selectedLang]}</NormalText>
                    <NormalText>{LABELS.aboutYouDescription2[selectedLang]}</NormalText>
                </Stack>
                <Controller
                    control={control}
                    name={`irInsuranceFor`}
                    render={({
                        field: { onChange, onBlur, value, name },
                        fieldState: { error },
                    }) => (
                        <Selector
                            data-testid="IrInsuranceForBtn"
                            id={name}
                            label={LABELS.question1[selectedLang].question}
                            aria-label={LABELS.question1[selectedLang].ariaLabel}
                            aria-required={true}
                            values={LABELS.question1[selectedLang].options}
                            customStyle={{
                                rootStyle: {
                                    width: '50%',
                                    media: [
                                        {
                                            maximum: BREAKPOINTS.TABLET_LANDSCAPE,
                                            width: '100%',
                                        },
                                    ],
                                },
                            }}
                            selected={value}
                            onBlur={onBlur}
                            onChange={onChange}
                            errors={error && [error.message]}
                        />
                    )}
                />
                <Stack direction="row" gapX={rem(140)} align="flex-start" style={{ alignSelf: "stretch" }}>
                    <IrAboutYouInputs forWhom={eForWhom.you} />
                    {selectedIrInsuranceFor === eIncluded.meAndPartner && <IrAboutYouInputs forWhom={eForWhom.partner} />}
                </Stack>

                <Stack direction="row" gapX={rem(140)} align="flex-start" style={{ alignSelf: "stretch" }}>
                    <Stack
                        align="flex-start"
                        style={{
                            width: windowWidth > BREAKPOINTS.TABLET_LANDSCAPE ? LABELS.colWidth : "100%"
                        }}
                    >
                        <Controller
                            control={control}
                            name={`irChildCount`}
                            rules={{
                                required: LABELS.errors.required[selectedLang]
                            }}
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { error },
                            }) => (
                                <TextInput
                                    data-testid={`irChildCount`}
                                    label={LABELS.question8[selectedLang].question}
                                    aria-label={LABELS.question8[selectedLang].ariaLabel}
                                    customStyle={{
                                        rootStyle: {
                                            width: LABELS.colWidth,
                                            media: [
                                                {
                                                    maximum: BREAKPOINTS.TABLET_LANDSCAPE,
                                                    width: '100%',
                                                },
                                            ],
                                        },
                                    }}
                                    ref={ref}
                                    value={value}
                                    onBlur={(event) => {
                                        /* onChange */
                                        const countInNumber = Number(getOnlyNumbers(event.target.value));
                                        if (countInNumber > num10) {
                                            onChange(String(num10));
                                        } else {
                                            onChange(String(countInNumber));
                                        }
                                        onBlur()
                                    }}
                                    onChange={(newValue) => onChange(getOnlyNumbers(newValue))}
                                    errors={error && [error.message]}
                                    callouts={false}
                                />
                            )}
                        />
                    </Stack>
                    {
                        Number(enteredIrChildCount) > 0 && <Stack
                            align="flex-start"
                            style={{
                                width: windowWidth > BREAKPOINTS.TABLET_LANDSCAPE ? LABELS.colWidth : "100%"
                            }}
                        >
                            <Controller
                                control={control}
                                name={`irHaveMoney`}
                                render={({
                                    field: { onChange, onBlur, value, name },
                                    fieldState: { error },
                                }) => (
                                    <Selector
                                        id={name}
                                        label={LABELS.question9[selectedLang].question}
                                        aria-label={LABELS.question9[selectedLang].ariaLabel}
                                        aria-required={true}
                                        values={LABELS.question9[selectedLang].options}
                                        selected={value}
                                        onBlur={onBlur}
                                        onChange={onChange}
                                        errors={error && [error.message]}
                                        callouts={false}
                                    />
                                )}
                            />
                        </Stack>
                    }

                </Stack>
                <Button
                    data-testid="submitBtn"
                    saving={false}
                    disabled={false}
                    customStyle={{
                        buttonStyle: {
                            media: [
                                {
                                    maximum: BREAKPOINTS.TABLET_PORTRAIT,
                                    width: "100%",
                                },
                            ],
                        },
                    }}
                    aria-label={LABELS.aboutYouSubmitBtn[selectedLang].ariaLabel}
                >
                    {LABELS.aboutYouSubmitBtn[selectedLang].label}
                </Button>
            </Stack>
        </FormProvider>
    )
}

// Wrapper component that provides the context
function IrAboutYouForm() {
    return (
        <FormFocusProvider>
            <IrAboutYouFormContent />
        </FormFocusProvider>
    );
}

export default IrAboutYouForm